import React, { useState, useEffect, useMemo, Component } from "react";
import Select, { components } from "react-select";
import { useSelector, useDispatch } from "react-redux";
import { FixedSizeList as List } from "react-window";
import {
  white,
  textPrimary,
  textSecondary,
  newBorder,
  primary,
  hoverItemColor,
  backgroundLight,
} from "../../utils/colors";
import { useTranslation } from "react-i18next";
import { Flex, Box, Text, Spinner, Avatar } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { getAllPatientsList } from "../../actions/patientActions";
import StatusBadge from "../badges/StatusBadge";
import dayjs from "dayjs";
import SearchInput from "../input/SearchInput";
import { filter } from "lodash";
import { useParams } from "react-router-dom";
import useIsMobile from "../../hooks/useIsMobile";
import StatusBadgeNew from "../badges/StatusBadgeNew";

// REACT WINDOW
// class MenuList extends Component {
//   render() {
//     const { options, children, getValue } = this.props;
//     const [value] = getValue();
//     const initialOffset = options.indexOf(value) * height;

//     // If no options, show a "No Results Found" message
//     if (options.length === 0) {
//       return (
//         <Flex
//           align="center"
//           justify="center"
//           h="48px"
//           borderBottom={`1px solid ${newBorder}`}
//           color={textSecondary}
//         >
//           No Results Found
//         </Flex>
//       );
//     }

//     // Calculate height dynamically
//     const itemCount = children.length;
//     const calculatedHeight = itemCount * height; // Height based on number of options
//     const maxHeight = 369; // Set a max height
//     const finalHeight = Math.min(calculatedHeight, maxHeight); // Ensure it does not exceed maxHeight

//     return (
//       <List
//         height={finalHeight}
//         itemCount={itemCount}
//         itemSize={height}
//         initialScrollOffset={initialOffset}
//       >
//         {({ index, style }) => (
//           <Flex
//             h="100%"
//             style={style}
//             borderBottom={`1px solid ${newBorder}`}
//             _last={{
//               borderBottom: "none",
//             }}
//           >
//             {children[index]}
//           </Flex>
//         )}
//       </List>
//     );
//   }
// }

const MenuList = (props) => {
  const { options, children, getValue, selectProps } = props;
  const isMobile = selectProps.isMobile;

  // console.log("isMobile", isMobile);
  const height = isMobile ? 72 : 63; // <- Mobile height
  // console.log("height", height);

  const [value] = getValue();
  const initialOffset = options.indexOf(value) * height;

  if (options.length === 0) {
    return (
      <Flex
        align="center"
        justify="center"
        h="48px"
        borderBottom={`1px solid ${newBorder}`}
        color={textSecondary}
      >
        No Results Found
      </Flex>
    );
  }

  const itemCount = children.length;
  const calculatedHeight = itemCount * height;
  const maxHeight = 369;
  const finalHeight = Math.min(calculatedHeight, maxHeight);

  return (
    <List
      height={finalHeight}
      itemCount={itemCount}
      itemSize={height}
      initialScrollOffset={initialOffset}
    >
      {({ index, style }) => (
        <Flex h="100%" style={style} borderBottom={`1px solid ${newBorder}`}>
          {children[index]}
        </Flex>
      )}
    </List>
  );
};

const PatientSelectNew = ({ onChange, setAddPatientModalOpen }) => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const getAllPatients = useSelector((state) => state.getAllPatients);
  const { loading, error, patients } = getAllPatients;
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  // FILTERED
  const filteredOptions = useMemo(() => {
    // Normalize the input for case-insensitive comparison
    const normalizedInput = inputValue.trim().toLowerCase();

    const filtered = patients
      .filter((patient) => {
        // Concatenate and normalize patient's full name and patientID for comparison
        const fullName =
          `${patient.firstNameNormalized} ${patient.lastNameNormalized}`.toLowerCase();
        const patientID = patient.patientID
          ? patient.patientID.toLowerCase()
          : "";

        // Check if the normalized full name or patientID includes the normalized input
        return (
          !normalizedInput ||
          fullName.includes(normalizedInput) ||
          patientID.includes(normalizedInput)
        );
      })

      .map((patient) => ({
        value: patient._id,
        label:
          patient.firstName && patient.lastName
            ? `${patient.firstName} ${patient.lastName}`
            : "Loading...",
        firstName: patient.firstName,
        lastName: patient.lastName,
        patientID: patient.patientID,
        email: patient.email,
        phone: patient.phone,
        birthDate: patient.birthDate,
        status: patient.status,
      }));

    // Optionally limit the number of results here if needed
    // return filtered.slice(Math.max(filtered.length - 50, 0));
    return filtered.slice(0, 50);
  }, [inputValue, patients]);

  useEffect(() => {
    // Fetch patients once when the component mounts
    dispatch(getAllPatientsList("", slug));
  }, [dispatch, slug]);

  const handleChange = (selectedOption) => {
    if (onChange) {
      // console.log("selectedOption", selectedOption);
      onChange(selectedOption);
    }
    setAddPatientModalOpen(false);
  };

  // Custom rendering for options
  const formatOptionLabel = ({ firstName, lastName, birthDate, status }) => (
    <Flex
      align="center"
      justify="space-between"
      w="100%"
      h="100%"
      p={["0 0.5rem", "0 0.5rem"]}
    >
      <Flex align="center" h="100%">
        <Avatar
          h={["2rem", "1.5rem"]}
          w={["2rem", "1.5rem"]}
          fontSize="xs"
          name={`${lastName} ${firstName}`}
          // bg="#dceeff"
          // color="#3a74a9"
          bg={textPrimary}
          color={white}
          mr="0.5rem"
        />

        <Text
          fontSize="md"
          fontWeight="500"
          color={textPrimary}
          maxW={["180px", "300px"]}
          isTruncated
        >
          {lastName}{" "}
          <Text as="span" fontWeight="400">
            {firstName}
          </Text>
        </Text>
        {!isMobile && (
          <Text fontSize="sm">
            <Text as="span" mx={2} fontWeight="200">
              |
            </Text>{" "}
            {dayjs(birthDate).format("YYYY-MM-DD")}
          </Text>
        )}
      </Flex>

      {/* <StatusBadge isSmall options="user" status={status} ml={2} /> */}
      <StatusBadgeNew
        fontSize="xs"
        p="0.25rem 1rem"
        options="patient"
        status={status}
        ml={2}
      />
    </Flex>
  );

  const CustomInput = () => null; // Render nothing for the default input

  return (
    <>
      {loading ? (
        <Flex w="100%" align="center" justify="center" p="3rem 0">
          <Spinner size="lg" color={primary} />
        </Flex>
      ) : (
        <Box
          w="100%"
          as={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, transition: 0.2 }}
        >
          <Flex w="100%">
            <SearchInput
              placeholder={t("patient.search")}
              onChange={handleInputChange}
              searchName={inputValue}
              p={["0 1.5rem", "0"]}
            />

            {/* <InputGroup w="100%" size="lg" fontSize="sm" borderRadius="10rem">
              <InputLeftElement
                pointerEvents="none"
                children={
                  <Flex
                    align="center"
                    justify="center"
                    h="1.75rem"
                    w="1.75rem"
                    bg={backgroundLight}
                    borderRadius="10rem"
                    
                  >
                    <Icon as={TbSearch} fontSize="1rem" color={textSecondary} />
                  </Flex>
                }
              />
              <Input
                fontSize="sm"
                placeholder={t("patient.search")}
                value={inputValue}
                onChange={handleInputChange}
                width="100%"
                borderRadius="10rem"
              />
            </InputGroup> */}
          </Flex>

          <Box
            w="100%"
            minH="369px"

            // bg={[backgroundLight, white]}
          >
            {isMobile && (
              <Text
                fontSize="0.875rem"
                fontWeight="500"
                color={textPrimary}
                p="1rem 1.5rem 0.5rem"
              >
                {t("patient.patients")}
              </Text>
            )}
            <Select
              autoFocus
              menuIsOpen
              isSearchable={false}
              isMobile={isMobile} // ✅ this is the key line
              components={{
                Input: CustomInput,
                MenuList,
              }}
              // components={{ MenuList }}
              options={filteredOptions}
              onInputChange={(newInputValue, { action }) => {
                if (action === "input-change") {
                  setInputValue(newInputValue);
                }
              }}
              onChange={handleChange}
              formatOptionLabel={formatOptionLabel}
              placeholder={t("placeholder.selectPatient")}
              filterOption={() => true} // Since you're manually filtering options, always return true here
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  // borderColor: isError
                  //   ? "red"
                  //   : state.isFocused
                  //   ? newBorder
                  //   : newBorder,
                  color: textPrimary,
                  display: "none",
                  borderRadius: "1rem",
                  backgroundColor: `${white}`,
                  border: `1px solid ${newBorder}`,
                  fontSize: isMobile ? "1rem" : "0.938rem",
                  fontWeight: "500",
                  height: isMobile ? "3rem" : "3rem",
                  padding: "2rem 0.25rem",
                  ":focus-visible": {
                    borderColor: primary,
                    boxShadow: `0 0 0 1px ${newBorder}`,
                  },
                  ":hover": {
                    cursor: "pointer",
                  },
                }),
                menu: (provided) => ({
                  ...provided,
                  position: "relative",
                  zIndex: 9999,
                  borderRadius: isMobile ? "0rem" : "1rem",
                  overflow: "hidden",
                  boxShadow: isMobile ? "none" : `0 0 0 1px ${newBorder}`,
                  borderTop: isMobile ? `1px solid ${newBorder}` : `none`,

                  height: "100%",
                }),
                menuList: (provided) => ({
                  ...provided,
                  borderRadius: "1rem",
                }),
                option: (provided, state) => ({
                  ...provided,

                  // height: "6rem",
                  // padding: isMobile ? "1rem 1.5rem" : "0.5rem 1.5rem",
                  // minHeight: isMobile ? "72px" : "54px",
                  borderBottom: isMobile ? "none" : `1px solid ${newBorder}`, // Border between options
                  // minHeight: "80px", // Match height used in react-window `height`
                  color: state.isSelected ? "white" : textSecondary, // Color of text in options
                  backgroundColor: state.isSelected ? primary : white, // Background color of selected option
                  ":hover": {
                    backgroundColor: state.isSelected
                      ? primary
                      : hoverItemColor, // Background color when hovering
                    // color: "white",
                    cursor: "pointer",
                  },
                  ":last-child": {
                    borderBottom: "none", // Remove border from last option
                  },
                }),
                singleValue: (provided, state) => {
                  const opacity = state.isDisabled ? 0.5 : 1;
                  const transition = "opacity 300ms";
                  const color =
                    state.data.value === "" ? "desired color" : "default color";

                  return { ...provided, opacity, transition, color };
                },
              }}
              noOptionsMessage={() => t("placeholder.noOptions")}
            />
            {filteredOptions.length > 49 && (
              <Flex w="100%" align="center" justify="center">
                <Text fontSize="xs" color={textSecondary} mt="1rem">
                  {t("patient.selectPatientNote")}
                </Text>
              </Flex>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default PatientSelectNew;
